import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useCabinetId } from "@/hooks/useCabinetMemberMe";
import { ErrorService } from "@/lib/errors";
import { apiClient } from "@/lib/apiClient";
import { resolveExternalDraftUpdate } from "@/utils/externalDraftUpdate";
import {
  VoiceTranscriptionPromptPayloadSchema,
  VoiceTranscriptionPromptResponseSchema,
  type VoiceTranscriptionPromptPayload,
  type VoiceTranscriptionPromptResponse,
} from "@hector/shared-types/voice-instructions";

type UpdateVoiceTranscriptionSettingsVariables = VoiceTranscriptionPromptPayload & {
  resetEditor: boolean;
};

/** General key for invalidating this personal setting across cabinet contexts. */
export const VOICE_TRANSCRIPTION_SETTINGS_KEY = "voice-transcription-settings";

/** Cabinet-scoped key used by the query and its precise invalidation. */
export const voiceTranscriptionSettingsKey = (cabinetId: string) =>
  [VOICE_TRANSCRIPTION_SETTINGS_KEY, cabinetId] as const;

/**
 * Fetches and edits the connected member's dictation-reformulation prompt.
 *
 * Three mechanisms coexist here and none is redundant — they answer distinct
 * failure modes:
 *
 *  - `isAwaitingFreshSettings` blocks the FIRST seed until the mount round-trip
 *    lands, so the editor never seeds from a cache up to 5 minutes old and then
 *    ignores the fresh value arriving a moment later.
 *  - `hasSeededEditor` marks that first seed as done, so the code below can tell
 *    "initial fill" from "the value changed under us".
 *  - `resolveExternalDraftUpdate` arbitrates every LATER response. This setting
 *    has a second writer — the agent's `set_voice_transcription_prompt` — and
 *    since 2026-08-12 the assistant bar opens the docked assistant instead of
 *    navigating to /assistant. Nothing unmounts this screen any more, so a
 *    seed-once-forever guard would leave the lawyer staring at the old text
 *    while Hector says « c'est appliqué ». The rule: adopt the server value when
 *    the editor is clean, never touch it when a draft is in progress — offer it
 *    instead, via `externalUpdate`.
 *
 * The refresh itself is pushed by `sessionDoneBridge.ts` (invalidation at the end
 * of an agent turn run from the case-less dock).
 */
export function useVoiceTranscriptionSettings() {
  const cabinetId = useCabinetId();
  const queryClient = useQueryClient();
  const hasSeededEditor = useRef(false);
  const editorCabinetId = useRef<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [savedPrompt, setSavedPrompt] = useState("");
  /**
   * Valeur écrite côté serveur pendant que l'avocat avait un brouillon en cours.
   * `null` = rien en attente. On la garde en state (et non dans la query) parce
   * que c'est une proposition faite À CET écran, qui disparaît dès que le
   * brouillon retombe — par « Charger la nouvelle version », par enregistrement,
   * ou parce que l'avocat a lui-même annulé sa frappe.
   */
  const [externalPrompt, setExternalPrompt] = useState<string | null>(null);

  const query = useQuery({
    queryKey: cabinetId
      ? voiceTranscriptionSettingsKey(cabinetId)
      : [VOICE_TRANSCRIPTION_SETTINGS_KEY],
    queryFn: async () =>
      VoiceTranscriptionPromptResponseSchema.parse(
        await apiClient.get<unknown>("/cabinet-members/voice-transcription-prompt"),
      ),
    enabled: Boolean(cabinetId),
    retry: 2,
    // 'always' ignores staleTime on mount: returning from /assistant remounts
    // this hook and must re-read what the agent just wrote.
    refetchOnMount: "always",
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  /**
   * True while the mount (or cabinet-switch) round-trip is still in flight.
   * `query.isLoading` is false as soon as a cached value exists, so it cannot
   * be used here — only `isFetching` reports the refetch forced by
   * `refetchOnMount:'always'`.
   */
  const isAwaitingFreshSettings = Boolean(cabinetId) && query.isFetching;

  useEffect(() => {
    if (editorCabinetId.current === cabinetId) return;

    // A cabinet switch changes the ownership boundary of this personal setting.
    // Drop both values before accepting B's query response so A's draft can
    // neither look valid nor be submitted while B is still loading.
    editorCabinetId.current = cabinetId;
    hasSeededEditor.current = false;
    setPrompt("");
    setSavedPrompt("");
    setExternalPrompt(null);
  }, [cabinetId]);

  useEffect(() => {
    if (!query.data || isAwaitingFreshSettings) return;

    const incoming = query.data.customPrompt ?? query.data.defaultPrompt;

    if (!hasSeededEditor.current) {
      setPrompt(incoming);
      setSavedPrompt(incoming);
      setExternalPrompt(null);
      hasSeededEditor.current = true;
      return;
    }

    const resolution = resolveExternalDraftUpdate({
      incoming,
      saved: savedPrompt,
      draft: prompt,
    });
    if (resolution.kind === "adopt") {
      setPrompt(resolution.value);
      setSavedPrompt(resolution.value);
      setExternalPrompt(null);
      return;
    }
    setExternalPrompt(resolution.kind === "offer" ? resolution.value : null);
  }, [query.data, isAwaitingFreshSettings, prompt, savedPrompt]);

  const updateMutation = useMutation({
    mutationFn: (variables: UpdateVoiceTranscriptionSettingsVariables) =>
      apiClient.patch<Pick<VoiceTranscriptionPromptResponse, "customPrompt">>(
        "/cabinet-members/voice-transcription-prompt",
        VoiceTranscriptionPromptPayloadSchema.parse({ prompt: variables.prompt }),
      ),
    onSuccess: (data, variables) => {
      const persistedPrompt = data.customPrompt ?? query.data?.defaultPrompt ?? "";
      setSavedPrompt(persistedPrompt);

      if (variables.resetEditor) {
        setPrompt(persistedPrompt);
      }

      if (cabinetId) {
        void queryClient.invalidateQueries({
          queryKey: voiceTranscriptionSettingsKey(cabinetId),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: [VOICE_TRANSCRIPTION_SETTINGS_KEY],
      });

      toast.success(
        variables.resetEditor
          ? "Consigne de dictée réinitialisée."
          : "Consigne de dictée enregistrée.",
      );
    },
    onError: (error) => {
      ErrorService.handle(
        error instanceof Error
          ? error
          : new Error("La sauvegarde de la consigne de dictée a échoué."),
        {
          userMessage:
            "Votre consigne de dictée n'a pas été enregistrée. Veuillez réessayer.",
          context: { action: "update_voice_transcription_prompt" },
        },
      );
    },
  });

  const updatePrompt = useCallback(
    (nextPrompt: string) => {
      if (nextPrompt.length <= (query.data?.maxCharacters ?? 0)) {
        setPrompt(nextPrompt);
      }
    },
    [query.data?.maxCharacters],
  );

  const save = useCallback(() => {
    updateMutation.mutate({ prompt, resetEditor: false });
  }, [prompt, updateMutation]);

  const reset = useCallback(() => {
    updateMutation.mutate({ prompt: null, resetEditor: true });
  }, [updateMutation]);

  /** Remplace le brouillon par la version écrite entre-temps — geste explicite. */
  const loadExternalPrompt = useCallback(() => {
    if (externalPrompt === null) return;
    setPrompt(externalPrompt);
    setSavedPrompt(externalPrompt);
    setExternalPrompt(null);
  }, [externalPrompt]);

  return {
    ...query,
    // Overrides `query.isLoading`, which is false as soon as a cached value
    // exists: without this the editor would flash empty (the seed is held back
    // by `isAwaitingFreshSettings`) during the mount refetch. Once seeded, a
    // background refetch must NOT put the editor back into a loading state —
    // the lawyer may be typing.
    isLoading: query.isLoading || (isAwaitingFreshSettings && !hasSeededEditor.current),
    prompt,
    updatePrompt,
    characterCount: prompt.length,
    isDirty: prompt !== savedPrompt,
    save,
    reset,
    isSaving: updateMutation.isPending,
    /**
     * Non-`null` uniquement quand la consigne a changé côté serveur ALORS que
     * l'avocat avait un brouillon : l'écran doit le lui dire sans rien écraser.
     */
    externalUpdate:
      externalPrompt === null ? null : { value: externalPrompt, load: loadExternalPrompt },
  };
}
